// Pitch -> staff position. Staff placement is *diatonic*: a note's vertical
// position depends on its letter (spelling), not its pitch class, so D#4 and
// Eb4 are the same sound but sit on different lines. We therefore spell via the
// existing key-aware spelling in notes.ts. See SPEC.md §4.

import { parseKey, spellNote, accidentalForKey } from '@/music/notes';
import { spellMidi } from '@/music/score/pitch';

/** Accidental a notehead's own spelling implies, in isolation. */
export type AccidentalKind = 'sharp' | 'flat';

const LETTER_ORDER: Record<string, number> = { C: 0, D: 1, E: 2, F: 3, G: 4, A: 5, B: 6 };

/** Scientific octave for a MIDI number (C4 = middle C = 60). */
export function octaveOf(midi: number): number {
  return Math.floor(midi / 12) - 1;
}

/**
 * Diatonic staff index: octave*7 + letterOrder(C=0..B=6). Monotonic in pitch
 * height by *letter*, so consecutive lines/spaces differ by 1. E4 = 30.
 */
export function diatonicIndex(letter: string, octave: number): number {
  const order = LETTER_ORDER[letter.toUpperCase()];
  if (order === undefined) throw new Error(`diatonicIndex: bad letter "${letter}"`);
  return octave * 7 + order;
}

export type Clef = 'treble' | 'treble8vb' | 'bass' | 'alto' | 'tenor';

/** Diatonic index of each clef's bottom staff line.
 *  Treble: E4. Bass: G2. Alto: F3 (C4 mid-line). Tenor: D3 (C4 on line 4).
 *  Treble-8vb (the guitar convention) WRITES identically to treble — the
 *  little 8 only says it SOUNDS an octave lower — so it shares the table row. */
const BOTTOM_LINE: Record<Clef, number> = {
  treble: diatonicIndex('E', 4), // 30
  treble8vb: diatonicIndex('E', 4), // 30
  bass: diatonicIndex('G', 2), // 18
  alto: diatonicIndex('F', 3), // 24
  tenor: diatonicIndex('D', 3), // 22
};

/** Diatonic index of a clef's bottom line (for inverse position → pitch). */
export function clefBottomLine(clef: Clef = 'treble'): number {
  return BOTTOM_LINE[clef];
}

export interface StaffPitchOptions {
  /** Song key label ("Eb", "G minor", …). Chooses sharp vs flat spelling. */
  key?: string;
  /** Explicit spelling ("Eb", "F#") that overrides the key-derived one. */
  spelledAs?: string;
  /** Clef the position is measured against. Defaults to treble. */
  clef?: Clef;
}

/** The written letter, octave, and any accidental for a MIDI note. */
export function spelledNote(
  midi: number,
  opts: StaffPitchOptions = {},
): { letter: string; octave: number; accidental: AccidentalKind | null } {
  const name = opts.spelledAs ?? spellNote(midi % 12, accidentalForKey(parseKey(opts.key)));
  // The sounding octave can differ from the written one: Cb5 sounds B4,
  // while B#4 sounds C5. Recover the octave from the complete spelling.
  try {
    const pitch = spellMidi(midi, { name });
    const accidental: AccidentalKind | null = pitch.alter > 0
      ? 'sharp'
      : pitch.alter < 0
        ? 'flat'
        : null;
    return { letter: pitch.step, octave: pitch.octave, accidental };
  } catch {
    // Legacy chart notes may retain a mismatched spelling (for example when
    // transposition clamps MIDI at 0/127). Keep rendering them as before.
    return {
      letter: name[0].toUpperCase(),
      octave: octaveOf(midi),
      accidental: name.includes('#') ? 'sharp' : name.includes('b') ? 'flat' : null,
    };
  }
}

/**
 * Vertical position in staff-spaces above the treble bottom line (E4 = 0),
 * positive upward. Each diatonic step is half a staff-space, so integer
 * offsets fall on lines and half-integers in spaces. Multiply by spaceSize
 * (px) at render time.
 */
export function staffOffset(midi: number, opts: StaffPitchOptions = {}): number {
  const { letter, octave } = spelledNote(midi, opts);
  return (diatonicIndex(letter, octave) - BOTTOM_LINE[opts.clef ?? 'treble']) * 0.5;
}

/**
 * Ledger lines needed for a note, as staff-space offsets (integers). Above the
 * top line (offset > 4) or below the bottom line (offset < 0). Empty when the
 * note sits within the staff.
 */
export function ledgerLineOffsets(offset: number): number[] {
  const lines: number[] = [];
  if (offset >= 5) for (let y = 5; y <= Math.floor(offset); y++) lines.push(y);
  if (offset <= -1) for (let y = -1; y >= Math.ceil(offset); y--) lines.push(y);
  return lines;
}
